import { debounce } from "lodash";
import { FC, memo, useEffect, useMemo, useRef, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { SplitterStyled } from "@ryde/controls";
import { useCompanyScopeContext, useRtl } from "@ryde/redux";
import {
  cardRideIdsSelector,
  cardRidesSelector,
  GlobalAppEventEnum,
  mapOpenAF,
  MapRouteBlockTypeEnum,
  resolveSelected,
  RidesViewTypeEnum,
  selectedTableRowsIdsAtomFamily,
  selectedTableRowsIdsSetSF,
} from "@ryde/shared";

import { RidesRoutes } from "../../routes";
import { KanbanMap } from "../common";
import { CardBoardList, CardBoardNoData } from "./components";
import { RidesCardBoardStyled } from "./rides-card-board.styled";

const KanbanMapMemoized = memo(KanbanMap);
const CardBoardListMemoized = memo(CardBoardList);

const CONSTRAINTS_PERCENT = {
  min: 10,
  max: 90,
} as const;
const DEFAULT_DIVIDER_VALUE_PERCENTAGE_MAP_OPEN = 65;
const DEFAULT_DIVIDER_VALUE_PERCENTAGE_MAP_CLOSE = 100;
const MAP_RESIZE_THROTTLE_MS = 300;

export const RidesCardBoard: FC = () => {
  const rows = useRecoilValue(cardRidesSelector);
  const cardRideIds = useRecoilValue(cardRideIdsSelector);
  const setSelectedTableRowsIds = useSetRecoilState(
    selectedTableRowsIdsAtomFamily("ride"),
  );
  const selectedTableRowsIdsSet = useRecoilValue(
    selectedTableRowsIdsSetSF("ride"),
  );

  const isMapOpen = useRecoilValue(mapOpenAF(RidesViewTypeEnum.CardBoard));
  const [dividerXPercentage, setDividerXPercentage] = useState(
    isMapOpen
      ? DEFAULT_DIVIDER_VALUE_PERCENTAGE_MAP_OPEN
      : DEFAULT_DIVIDER_VALUE_PERCENTAGE_MAP_CLOSE,
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { eventEmitter } = useCompanyScopeContext();
  const { isRtl } = useRtl();

  const mapInvalidateSizeDebounced = useMemo(
    () =>
      debounce(
        () => eventEmitter?.emit(GlobalAppEventEnum.RESIZE_RIDE_MAP),
        MAP_RESIZE_THROTTLE_MS,
      ),
    [eventEmitter],
  );

  const handleDrag = (event: MouseEvent): void => {
    if (!containerRef.current) {
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    const containerRect = containerRef.current.getBoundingClientRect();

    const offsetX = isRtl
      ? containerRect.right - event.clientX
      : event.clientX - containerRect.left;

    const newDividerX = Math.max(
      CONSTRAINTS_PERCENT.min,
      Math.min(CONSTRAINTS_PERCENT.max, (offsetX / containerWidth) * 100),
    );
    setDividerXPercentage(newDividerX);
    mapInvalidateSizeDebounced();
  };

  const handleMouseDown = (): void => {
    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseUp = (): void => {
    document.removeEventListener("mousemove", handleDrag);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  useEffect(() => {
    setDividerXPercentage(
      isMapOpen
        ? DEFAULT_DIVIDER_VALUE_PERCENTAGE_MAP_OPEN
        : DEFAULT_DIVIDER_VALUE_PERCENTAGE_MAP_CLOSE,
    );
  }, [isMapOpen]);

  useEffect(() => {
    resolveSelected({
      selectedTableRowsIds: selectedTableRowsIdsSet,
      setSelectedTableRowsIds,
      tableIds: cardRideIds,
    });
    // We want an effect only on ids grid ids changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardRideIds]);

  return (
    <>
      <RidesCardBoardStyled
        ref={containerRef}
        $leftColumn={dividerXPercentage}
        className="thin_scrollbar"
      >
        <div className="card-board-left-column">
          <div className="card-board-scroll-shell">
            {!rows.length && <CardBoardNoData />}
            {Boolean(rows.length) && <CardBoardListMemoized rides={rows} />}
          </div>
        </div>

        {isMapOpen && (
          <div className="card-board-map-wrapper">
            <SplitterStyled onMouseDown={handleMouseDown} />
            <div className="map_scrollable_content">
              <KanbanMapMemoized
                routeBlockType={MapRouteBlockTypeEnum.CardBoard}
              />
            </div>
          </div>
        )}
      </RidesCardBoardStyled>
      <RidesRoutes />
    </>
  );
};
